"use client";

// Client-only renderer for the substrate detail-page's "overview" dl.
//
// The page-factory's `makeDetailPage(def)` returns a server component that
// reads the row from the registry-driven Drizzle table. The dl block lives
// inside that server tree but the field labels come from the merged module
// i18n bundle, which is wired through `NextIntlClientProvider` — a client-
// only context. Splitting the dl into this `"use client"` island lets us
// resolve labels via `useTranslations()` while keeping the server component
// responsible for data loading.
//
// Field VALUES are pre-stringified on the server side (raw cells, JSON-safe)
// and date-typed cells are pre-formatted using the platform-i18n locale —
// the wrapper component above converts Drizzle row objects into the
// `EntityDetailFieldSpec[]` shape this component renders.

import * as React from "react";
import { useLocale, useT, formatDate } from "@aio/platform-i18n";
import type { FieldKind } from "../builders/types";

export interface EntityDetailFieldSpec {
  /** Field name (snake_case) — also used as the React key. */
  name: string;
  /** Dotted i18n key for the field's display label. */
  labelKey: string;
  /** Field kind so we can pick the right Intl branch when formatting. */
  kind: FieldKind;
  /**
   * Pre-extracted raw value. Dates arrive as ISO strings (Next 16's server-
   * to-client serializer rejects raw Date instances), numbers as numbers,
   * everything else as a string or null.
   */
  value: unknown;
}

export interface EntityDetailFieldsProps {
  fields: readonly EntityDetailFieldSpec[];
  /** Pre-resolved status string (translated server-side via the same hook). */
  statusLabelKey: string;
  status: string;
}

function isIsoDateLike(s: string): boolean {
  // Match the strings Next 16's server serializer emits for Date columns
  // (always full ISO 8601 with milliseconds and a Z or +HH:MM offset).
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2}))?$/.test(s);
}

function renderValue(
  value: unknown,
  kind: FieldKind,
  locale: "sr-Latn" | "sr-Cyrl" | "en",
): string {
  if (value === null || value === undefined || value === "") return "";
  if (kind === "timestamp") {
    const d = value instanceof Date ? value : typeof value === "string" && isIsoDateLike(value) ? new Date(value) : null;
    if (d && !Number.isNaN(d.getTime())) {
      return formatDate(locale, d, { dateStyle: "medium", timeStyle: "short" });
    }
  }
  if (kind === "boolean") return value ? "Da" : "Ne";
  return String(value);
}

export function EntityDetailFields({
  fields,
  statusLabelKey,
  status,
}: EntityDetailFieldsProps): React.ReactElement {
  const t = useT();
  const { locale } = useLocale();

  return (
    <dl className="grid grid-cols-2 gap-2 text-sm">
      {fields.map((f) => (
        <div key={f.name} className="contents">
          <dt className="text-fg-muted">{t(f.labelKey)}</dt>
          <dd className="text-fg">{renderValue(f.value, f.kind, locale)}</dd>
        </div>
      ))}
      <div className="contents">
        <dt className="text-fg-muted">{t(statusLabelKey)}</dt>
        <dd className="text-fg">
          {(() => {
            // Resolve the lifecycle state value through the platform-namespace
            // bundle so users see "U pripremi" instead of the raw enum
            // `draft`. Falls back to the raw value when the bundle lacks an
            // entry — matches the existing missing-key policy elsewhere.
            const stateKey = `platform.lifecycle.state.${status}`;
            const resolved = t(stateKey);
            return resolved === stateKey ? status : resolved;
          })()}
        </dd>
      </div>
    </dl>
  );
}
